'use client';

import Link from 'next/link';
import { Users, Hash, Lock, KeyRound } from 'lucide-react';
import { Button as DsButton } from '@/components/ds';
import { cn } from '@/lib/utils';
import { getInitials } from '@/lib/displayName';

export type GroupCardData = {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  privacy: 'public' | 'request' | 'private';
  memberCount: number;
  channelCount?: number; // always 1 in Phase 1
  isJoined: boolean;
  isOwner?: boolean;
  lastMessageAt?: string | null;
  // Visual identity comes ONLY from the curated banner enum, resolved through
  // the shared --hdr-bg pattern (data-banner sets --bn-deep/--bn-glow; the
  // theme sheet builds --hdr-bg). Works in both Booth and Daylight.
  banner?: string | null;
  // Optional online count for the floating presence pill (presence not wired
  // yet, so the list endpoint omits it and the pill is hidden).
  onlineCount?: number | null;
  // Viewer's join-request status for request-privacy groups (server-driven).
  // 'denied' is treated as "can request again".
  requestStatus?: 'none' | 'pending' | 'approved' | 'denied' | null;
};

type Props = {
  group: GroupCardData;
  onJoin?: (id: string) => void;
  onRequest?: (id: string) => void;
  onCancelRequest?: (id: string) => void;
  layout?: 'grid' | 'list';
};

function formatActiveAgo(iso?: string | null): string | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 0) return 'just now';
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.floor(d / 7)}w`;
  return `${Math.floor(d / 30)}mo`;
}

// Visibility = a real signal — three distinct treatments, not three labels.
// `float` reads legibly over the banner in both themes.
function VisibilityChip({
  privacy,
  float = false,
}: {
  privacy: GroupCardData['privacy'];
  float?: boolean;
}) {
  if (privacy === 'private') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-pill-active-bg text-pill-active-text">
        <Lock className="w-3 h-3" strokeWidth={2} />
        Private
      </span>
    );
  }
  if (privacy === 'request') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-status-away/15 text-status-away border border-status-away/30">
        <KeyRound className="w-3 h-3" strokeWidth={2} />
        Request
      </span>
    );
  }
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium border border-border-subtle',
        float ? 'bg-surface-0 text-text-secondary' : 'bg-surface-2 text-text-secondary',
      )}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-status-online" aria-hidden />
      Public
    </span>
  );
}

function bannerInitial(name: string): string {
  const c = name.trim().charAt(0);
  return c ? c.toUpperCase() : '#';
}

export default function GroupCard({
  group,
  onJoin,
  onRequest,
  onCancelRequest,
  layout = 'grid',
}: Props) {
  const banner = group.banner || 'default';
  const channels = group.channelCount ?? 1;
  const activeAgo = formatActiveAgo(group.lastMessageAt);

  const cta = (() => {
    if (group.isJoined || group.isOwner) {
      return (
        <Link href={`/groups/${group.id}`} className="contents">
          <DsButton variant="secondary" size="sm" className="w-full justify-center">
            Open
          </DsButton>
        </Link>
      );
    }
    if (group.privacy === 'public') {
      return (
        <DsButton
          variant="primary"
          size="sm"
          className="w-full justify-center"
          onClick={() => onJoin?.(group.id)}
        >
          Join group
        </DsButton>
      );
    }
    if (group.privacy === 'request') {
      // Server-driven: a pending row shows the pending state + cancel; a denied
      // (or absent) row reads as "can request again".
      if (group.requestStatus === 'pending') {
        return (
          <div className="flex flex-col gap-1">
            <DsButton variant="secondary" size="sm" disabled className="w-full justify-center">
              Requested · pending
            </DsButton>
            <button
              type="button"
              onClick={() => onCancelRequest?.(group.id)}
              className="text-[11px] text-text-tertiary hover:text-text-primary"
            >
              Cancel request
            </button>
          </div>
        );
      }
      return (
        <DsButton
          variant="secondary"
          size="sm"
          leftIcon="KeyRound"
          className="w-full justify-center"
          onClick={() => onRequest?.(group.id)}
        >
          Request to join
        </DsButton>
      );
    }
    return (
      <DsButton
        variant="secondary"
        size="sm"
        leftIcon="Lock"
        disabled
        className="w-full justify-center"
      >
        Invite only
      </DsButton>
    );
  })();

  if (layout === 'list') {
    return (
      <div className="flex items-center gap-4 px-4 py-3 hover:bg-surface-3 transition-colors">
        <div
          data-banner={banner}
          className="w-9 h-9 rounded-md flex-shrink-0 flex items-center justify-center text-xs font-semibold text-text-primary"
          style={{ background: 'var(--hdr-bg)' }}
          aria-hidden
        >
          {bannerInitial(group.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              href={`/groups/${group.id}`}
              className="text-sm font-medium text-text-primary truncate hover:text-accent"
            >
              {group.name}
            </Link>
            <VisibilityChip privacy={group.privacy} />
          </div>
          <p className="text-xs text-text-secondary truncate mt-0.5">
            {group.description || 'No description.'}
          </p>
        </div>
        <div className="hidden sm:flex items-center gap-3 text-xs text-text-tertiary">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
            {group.memberCount}
          </span>
          <span className="inline-flex items-center gap-1">
            <Hash className="w-3.5 h-3.5" strokeWidth={1.75} />
            {channels}
          </span>
          {activeAgo && <span className="font-mono">active {activeAgo}</span>}
        </div>
        <div className="w-28 flex-shrink-0">{cta}</div>
      </div>
    );
  }

  return (
    // No banner band — the group's palette appears ONLY on the fill-avatar tile
    // in the identity row. Everything sits on the normal card surface.
    <article className="rounded-md border border-border-subtle bg-surface-2 overflow-hidden flex flex-col hover:border-border-default transition-colors">
      <div className="px-4 pt-3 pb-3 flex flex-col gap-2 flex-1">
        {/* 1. Header row — visibility chip left, online-count pill right. */}
        <div className="flex items-center justify-between gap-2 min-h-[20px]">
          <VisibilityChip privacy={group.privacy} />
          {typeof group.onlineCount === 'number' && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm text-[11px] font-medium bg-surface-3 text-text-secondary border border-border-subtle">
              <span className="w-1.5 h-1.5 rounded-full bg-status-online" aria-hidden />
              {group.onlineCount} online
            </span>
          )}
        </div>

        {/* 2. Identity row — banner-tinted fill avatar + name + active {t}. */}
        <div className="flex items-center gap-3">
          <div
            data-banner={banner}
            aria-hidden
            className="bn-fill w-11 h-11 rounded-md flex-shrink-0 flex items-center justify-center text-base font-bold text-text-primary"
          >
            {getInitials(group.name)}
          </div>
          <Link
            href={`/groups/${group.id}`}
            className="min-w-0 flex-1 text-sm font-semibold text-text-primary truncate hover:text-accent"
          >
            {group.name}
          </Link>
          {activeAgo && (
            <span className="text-[11px] font-mono text-text-tertiary flex-shrink-0">
              active {activeAgo}
            </span>
          )}
        </div>

        {/* 3. Description — 2-line clamp, locked min-height for grid alignment. */}
        <p
          className="text-xs text-text-secondary leading-relaxed"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            minHeight: '39px',
          }}
        >
          {group.description || 'No description yet.'}
        </p>

        {/* 4. Tags — up to 3. */}
        {group.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {group.tags.slice(0, 3).map((t) => (
              <span
                key={t}
                className="inline-flex items-center px-1.5 py-0.5 rounded-sm text-[11px] bg-surface-3 text-text-secondary border border-border-subtle"
              >
                #{t}
              </span>
            ))}
          </div>
        )}

        {/* 5. Stats — members + channels. */}
        <div className="flex items-center gap-3 text-xs text-text-tertiary mt-auto pt-1">
          <span className="inline-flex items-center gap-1">
            <Users className="w-3.5 h-3.5" strokeWidth={1.75} />
            {group.memberCount}
          </span>
          <span className="inline-flex items-center gap-1">
            <Hash className="w-3.5 h-3.5" strokeWidth={1.75} />
            {channels} {channels === 1 ? 'channel' : 'channels'}
          </span>
        </div>

        {/* 6. Context-aware CTA. */}
        <div className="mt-2">{cta}</div>
      </div>
    </article>
  );
}
